import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { AdminUser } from '../lib/authService'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Heart,
  Clock,
  CheckCircle,
  X,
  Check,
  Loader2,
  Sparkles,
  Calendar,
  EyeOff,
  Users,
} from 'lucide-react'

interface Testimony {
  id: string
  user_id: string
  church_id: string
  title: string
  testimony: string
  answered_date?: string
  is_public: boolean
  approval_status: 'pending' | 'approved' | 'rejected'
  approved_by?: string
  approved_at?: string
  rejection_reason?: string
  created_at: string
  testimony_type?: 'prayer' | 'evangelism'
  contact_name?: string
  salvation_date?: string
  evangelist_id?: string
}

interface TestimoniesProps {
  user: AdminUser | null
}

export default function Testimonies({ user: currentUser }: TestimoniesProps) {
  const [pendingTestimonies, setPendingTestimonies] = useState<Testimony[]>([])
  const [approvedTestimonies, setApprovedTestimonies] = useState<Testimony[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'pending' | 'approved'>('pending')
  const [rejectionReason, setRejectionReason] = useState('')
  const [rejectingId, setRejectingId] = useState<string | null>(null)
  const [withdrawingId, setWithdrawingId] = useState<string | null>(null)

  useEffect(() => {
    loadTestimonies()
  }, [currentUser])

  const loadTestimonies = async () => {
    if (!currentUser?.churchId) return

    try {
      setLoading(true)

      // Load pending testimonies
      const { data: pending, error: pendingError } = await supabase
        .from('prayer_testimonies')
        .select('*')
        .eq('church_id', currentUser.churchId)
        .eq('approval_status', 'pending')
        .order('created_at', { ascending: true })

      if (pendingError) throw pendingError
      setPendingTestimonies(pending || [])

      // Load approved testimonies
      const { data: approved, error: approvedError } = await supabase
        .from('prayer_testimonies')
        .select('*')
        .eq('church_id', currentUser.churchId)
        .eq('approval_status', 'approved')
        .is('withdrawn_at', null)
        .order('created_at', { ascending: false })
        .limit(50)

      if (approvedError) throw approvedError
      setApprovedTestimonies(approved || [])
    } catch (error) {
      console.error('Error loading testimonies:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleApprove = async (testimonyId: string) => {
    if (!currentUser?.id) return

    try {
      const { error } = await supabase
        .from('prayer_testimonies')
        .update({
          approval_status: 'approved',
          approved_by: currentUser.id,
          approved_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', testimonyId)

      if (error) throw error

      await loadTestimonies()
    } catch (error) {
      console.error('Error approving testimony:', error)
      alert('Failed to approve testimony')
    }
  }

  const handleReject = async (testimonyId: string) => {
    if (!currentUser?.id) return

    try {
      const { error } = await supabase
        .from('prayer_testimonies')
        .update({
          approval_status: 'rejected',
          approved_by: currentUser.id,
          rejection_reason: rejectionReason || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', testimonyId)

      if (error) throw error

      setRejectionReason('')
      setRejectingId(null)
      await loadTestimonies()
    } catch (error) {
      console.error('Error rejecting testimony:', error)
      alert('Failed to reject testimony')
    }
  }

  const handleWithdraw = async (testimonyId: string) => {
    if (!currentUser?.id) return

    try {
      const { error } = await supabase
        .from('prayer_testimonies')
        .update({
          withdrawn_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          approved_by: currentUser.id
        })
        .eq('id', testimonyId)

      if (error) throw error

      setWithdrawingId(null)
      await loadTestimonies()
    } catch (error) {
      console.error('Error withdrawing testimony:', error)
      alert('Failed to withdraw testimony')
    }
  }

  const renderTestimonyCard = (testimony: Testimony, showActions: boolean = true) => {
    const isEvangelism = testimony.testimony_type === 'evangelism'
    const displayName = isEvangelism
      ? testimony.contact_name || 'New Believer'
      : 'Church Member'

    return (
      <Card key={testimony.id} className="hover:shadow-md transition-shadow">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                isEvangelism ? 'bg-emerald-100' : 'bg-blue-100'
              }`}>
                {isEvangelism ? (
                  <Heart className="h-5 w-5 text-emerald-600" />
                ) : (
                  <Sparkles className="h-5 w-5 text-blue-600" />
                )}
              </div>
              <div>
                <p className="font-semibold">{testimony.title}</p>
                <p className="text-sm text-muted-foreground">
                  {displayName}
                </p>
              </div>
            </div>
            <Badge variant={isEvangelism ? 'default' : 'secondary'} className={
              isEvangelism ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : ''
            }>
              {isEvangelism ? (
                <><Users className="h-3 w-3 mr-1" /> Salvation</>
              ) : (
                <><Sparkles className="h-3 w-3 mr-1" /> Prayer</>
              )}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Testimony Content */}
          <div className={`p-4 rounded-lg ${isEvangelism ? 'bg-emerald-50' : 'bg-blue-50'}`}>
            <p className="text-muted-foreground whitespace-pre-wrap italic">
              "{testimony.testimony}"
            </p>
          </div>

          {/* Metadata */}
          <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
            {testimony.answered_date && (
              <div className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                <span>
                  {isEvangelism ? 'Salvation' : 'Answered'}: {new Date(testimony.answered_date).toLocaleDateString()}
                </span>
              </div>
            )}
            {testimony.salvation_date && isEvangelism && (
              <div className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                <span>Saved: {new Date(testimony.salvation_date).toLocaleDateString()}</span>
              </div>
            )}
            <div className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              <span>
                Submitted {new Date(testimony.created_at).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
            </div>
          </div>

          {/* Pending Actions */}
          {showActions && testimony.approval_status === 'pending' && (
            <div className="space-y-3 pt-2">
              {rejectingId === testimony.id ? (
                <div className="space-y-2">
                  <Textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Reason for rejection (optional)..."
                    rows={2}
                  />
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleReject(testimony.id)}
                      variant="destructive"
                      className="flex-1"
                    >
                      Confirm Reject
                    </Button>
                    <Button
                      onClick={() => {
                        setRejectingId(null)
                        setRejectionReason('')
                      }}
                      variant="outline"
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleApprove(testimony.id)}
                    className="flex-1"
                  >
                    <Check className="mr-2 h-4 w-4" />
                    Approve
                  </Button>
                  <Button
                    onClick={() => setRejectingId(testimony.id)}
                    variant="destructive"
                    className="flex-1"
                  >
                    <X className="mr-2 h-4 w-4" />
                    Reject
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Approved Actions */}
          {showActions && testimony.approval_status === 'approved' && (
            <div className="flex items-center justify-between pt-2">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Approved
                </Badge>
                {testimony.approved_at && (
                  <span className="text-xs text-muted-foreground">
                    {new Date(testimony.approved_at).toLocaleDateString()}
                  </span>
                )}
              </div>
              <Button
                onClick={() => setWithdrawingId(testimony.id)}
                variant="outline"
                size="sm"
              >
                <EyeOff className="mr-2 h-4 w-4" />
                Withdraw
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )
  }

  const prayerCount = [...pendingTestimonies, ...approvedTestimonies].filter(
    t => t.testimony_type === 'prayer' || !t.testimony_type
  ).length
  const salvationCount = [...pendingTestimonies, ...approvedTestimonies].filter(
    t => t.testimony_type === 'evangelism'
  ).length

  return (
    <div className="flex flex-col gap-4 p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Sparkles className="h-8 w-8 text-amber-500" />
            Testimonies
          </h1>
          <p className="text-muted-foreground">
            Moderate and manage prayer and salvation testimonies
          </p>
        </div>
        <div className="flex gap-4">
          <Card className="px-4 py-2">
            <div className="text-center">
              <div className="text-sm text-muted-foreground">Prayer</div>
              <div className="text-2xl font-bold text-blue-600">{prayerCount}</div>
            </div>
          </Card>
          <Card className="px-4 py-2">
            <div className="text-center">
              <div className="text-sm text-muted-foreground">Salvation</div>
              <div className="text-2xl font-bold text-emerald-600">{salvationCount}</div>
            </div>
          </Card>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'pending' | 'approved')} className="space-y-4">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="pending" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Pending Review
            {pendingTestimonies.length > 0 && (
              <Badge variant="secondary" className="ml-1 bg-amber-100 text-amber-700">
                {pendingTestimonies.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="approved" className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4" />
            Approved ({approvedTestimonies.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="space-y-4 mt-4">
          {pendingTestimonies.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <CheckCircle className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-lg font-medium">No pending testimonies</p>
                <p className="text-sm text-muted-foreground">
                  New testimonies will appear here for review
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {pendingTestimonies.map((t) => renderTestimonyCard(t))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="approved" className="space-y-4 mt-4">
          {approvedTestimonies.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Heart className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-lg font-medium">No approved testimonies yet</p>
                <p className="text-sm text-muted-foreground">
                  Approved testimonies will appear here
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {approvedTestimonies.map((t) => renderTestimonyCard(t))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Withdraw Confirmation Dialog */}
      <Dialog open={!!withdrawingId} onOpenChange={() => setWithdrawingId(null)}>
        <DialogContent className="bg-white dark:bg-gray-900">
          <DialogHeader>
            <DialogTitle>Withdraw Testimony</DialogTitle>
            <DialogDescription>
              This will hide the testimony from public view but preserve it in church records.
              Are you sure you want to continue?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setWithdrawingId(null)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => withdrawingId && handleWithdraw(withdrawingId)}
            >
              Withdraw
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
